import { Injectable, computed, inject, signal } from '@angular/core';
import { decodeJwt } from './jwt.util';
import { StoredSession, TokenPair } from './token-pair';
import { TokenStorageService } from './token-storage.service';

/** Seconds of clock skew tolerated before a token counts as expired. */
const EXPIRY_SKEW_MS = 30_000;

/**
 * Single source of truth for the admin session. The in-memory signal is authoritative;
 * sessionStorage only rehydrates it on reload. Holds the access token, the refresh token and
 * their expiry instants. Never logs or exposes tokens beyond the interceptor and the refresher.
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionStore {
  private readonly storage = inject(TokenStorageService);
  private readonly _session = signal<StoredSession | null>(this.rehydrate());
  /** Incremented on every start/clear so per-session caches (e.g. the admin profile) can tell sessions apart. */
  private readonly _generation = signal(0);

  readonly token = computed(() => this._session()?.accessToken ?? null);
  readonly refreshToken = computed(() => this._session()?.refreshToken ?? null);
  readonly accessTokenExpiresAt = computed(() => this._session()?.accessTokenExpiresAt ?? null);
  readonly generation = this._generation.asReadonly();
  readonly hasSession = computed(() => this._session() !== null);

  /** Access token present and not expired. Re-checks the clock on every call (signals cannot observe time). */
  isAuthenticated(now = Date.now()): boolean {
    const session = this._session();
    return session !== null && session.accessTokenExpiresAt - EXPIRY_SKEW_MS > now;
  }

  /** Refresh token present and not expired, so a silent refresh can still renew the session. */
  canRefresh(now = Date.now()): boolean {
    const session = this._session();
    return session !== null && session.refreshTokenExpiresAt - EXPIRY_SKEW_MS > now;
  }

  start(pair: TokenPair, now = Date.now()): void {
    const session: StoredSession = {
      accessToken: pair.accessToken,
      accessTokenExpiresAt: expiryOf(pair.accessToken, pair.accessTokenExpiresIn, now),
      refreshToken: pair.refreshToken,
      refreshTokenExpiresAt: expiryOf(pair.refreshToken, pair.refreshTokenExpiresIn, now),
    };
    this.storage.write(session);
    this._session.set(session);
    this._generation.update((value) => value + 1);
  }

  clear(): void {
    this.storage.clear();
    this._session.set(null);
    this._generation.update((value) => value + 1);
  }

  private rehydrate(): StoredSession | null {
    const session = this.storage.read();
    if (session && session.refreshTokenExpiresAt - EXPIRY_SKEW_MS > Date.now()) {
      return session;
    }
    this.storage.clear();
    return null;
  }
}

/**
 * Absolute expiry: the JWT `exp` claim when present (UX only, the API still decides), else
 * now + the `expiresIn` seconds the API returned. The earlier of the two wins.
 */
function expiryOf(token: string, expiresInSeconds: number, now: number): number {
  const fromTtl = now + Math.max(0, expiresInSeconds) * 1000;
  const exp = decodeJwt(token)?.exp;
  return typeof exp === 'number' ? Math.min(exp * 1000, fromTtl) : fromTtl;
}
